import { useQuery } from '@tanstack/react-query';
import { Select2 } from '@/components/forms/Select2';
import { Select2Async } from '@/components/forms/Select2Async';
import { Select2Multi } from '@/components/forms/Select2Multi';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import { FilterBar, type FilterField } from '@/components/common/FilterBar';
import { AsyncButton } from '@/components/common/AsyncButton';
import { filterDataApi, filterDataPaged } from '@/api/services/filterData';
import type { Select2Option } from '@/components/forms/Select2';
import type { SigmaFilterInput } from '@/api/types/sigma';

export interface SigmaFilterState {
  startDate: string;
  endDate: string;
  interval: number;
  symbols: string[];
  group: string | null;
  mifidClassification: string | null;
  executionVenue: string | null;
  groupProfile: string | null;
  groupText: string | null;
  countryId: number | null;
  portfolio: number | null;
  login: number | null;
  connectorId: number | null;
  uniqueId: string | null;
  highlight: number | null;
  byUniqueId: boolean;
  cumulativeProfit: boolean;
  live: boolean;
}

const INTERVAL_OPTIONS: Select2Option[] = [
  { value: 1, label: '1 Minute' },
  { value: 5, label: '5 Minutes' },
  { value: 15, label: '15 Minutes' },
  { value: 30, label: '30 Minutes' },
  { value: 60, label: '1 Hour' },
  { value: 240, label: '4 Hours' },
  { value: 1440, label: 'Daily' },
  { value: 10080, label: 'Weekly' },
  { value: 43200, label: 'Monthly' },
];

export function intervalLabel(interval: number): string {
  return INTERVAL_OPTIONS.find((o) => o.value === interval)?.label ?? 'N/A';
}

function fmtLocalNow(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}
const datePart = (v: string) => String(v).split('T')[0].split(' ')[0];
const withTime = (v: string, t: string) => (String(v).includes('T') ? String(v) : `${datePart(v)}T${t}`);

export function defaultSigmaFilters(): SigmaFilterState {
  const now = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return {
    startDate: fmt(start),
    endDate: fmt(now),
    interval: 1440,
    symbols: [],
    group: null,
    mifidClassification: null,
    executionVenue: null,
    groupProfile: null,
    groupText: null,
    countryId: null,
    portfolio: null,
    login: null,
    connectorId: null,
    uniqueId: null,
    highlight: null,
    byUniqueId: false,
    cumulativeProfit: false,
    live: false,
  };
}

export function toSigmaInput(f: SigmaFilterState, chartData: number): SigmaFilterInput {
  const daily = f.interval >= 1440;
  let startDate = daily ? `${datePart(f.startDate)}T00:00:00` : withTime(f.startDate, '00:00:00');
  let endDate = daily ? `${datePart(f.endDate)}T00:00:00` : withTime(f.endDate, '23:59:59');
  if (f.live && !daily) endDate = fmtLocalNow();
  return {
    startDate,
    endDate,
    interval: f.interval,
    group: f.group || null,
    mifidClassification: f.mifidClassification || null,
    executionVenue: f.executionVenue || null,
    countryId: f.countryId ?? 0,
    connectorId: f.byUniqueId ? 0 : f.connectorId ?? 0,
    login: f.byUniqueId ? 0 : f.login ?? 0,
    uniqueId: f.byUniqueId ? f.uniqueId || null : null,
    portfolio: f.byUniqueId ? null : f.portfolio ?? null,
    groupText: f.groupText || null,
    groupProfile: f.groupProfile || null,
    symbols: f.symbols.length ? f.symbols : null,
    byUniqueId: f.byUniqueId,
    cumulativeProfit: f.cumulativeProfit,
    chartData,
  };
}

// Mirrors sigma-index.js validation.
export function dateFiltersEmpty(f: SigmaFilterState): boolean {
  return !f.startDate || (!f.live && !f.endDate);
}
export function multipleFiltersChosen(f: SigmaFilterState): boolean {
  if (f.symbols.length) return false;
  let n = 0;
  if (f.mifidClassification) n++;
  if (f.executionVenue) n++;
  if (f.countryId) n++;
  if (f.group) n++;
  if (f.groupText || f.groupProfile) n++;
  if (f.login || f.connectorId) n++;
  if (f.uniqueId) n++;
  if (f.portfolio) n++;
  return n > 1;
}

interface Props {
  draft: SigmaFilterState;
  setDraft: React.Dispatch<React.SetStateAction<SigmaFilterState>>;
  onFetch: () => void;
  onExtract: () => void | Promise<unknown>;
  canConsolidated: boolean;
  fetching: boolean;
}

export function SigmaFilterBar({ draft, setDraft, onFetch, onExtract, canConsolidated, fetching }: Props) {
  const mifid = useQuery({ queryKey: ['rd-filter', 'mifid'], queryFn: filterDataApi.getMifidClassifications, staleTime: 5 * 60_000 });
  const venues = useQuery({ queryKey: ['rd-filter', 'venues'], queryFn: filterDataApi.getExecutionVenues, staleTime: 5 * 60_000 });
  const connectors = useQuery({ queryKey: ['rd-filter', 'nonTerminalConnectors'], queryFn: filterDataApi.getNonTerminalConnectors, staleTime: 5 * 60_000 });
  const portfolios = useQuery({ queryKey: ['rd-filter', 'portfolios'], queryFn: filterDataApi.getPortfolios, staleTime: 5 * 60_000 });
  const groupProfiles = useQuery({ queryKey: ['rd-filter', 'groupProfiles', 2], queryFn: () => filterDataApi.getGroupProfilesForPage(2), staleTime: 5 * 60_000 });
  const instruments = useQuery({ queryKey: ['rd-filter', 'instruments'], queryFn: filterDataApi.getInstruments, staleTime: 5 * 60_000 });

  const intraday = draft.interval < 1440;
  const str = (v: string | number | null) => (v == null ? '' : String(v));
  const uid = draft.byUniqueId;
  const pfLocked = draft.portfolio != null;
  // Portfolio exclusivity: when a portfolio is chosen the other dimension filters are
  // cleared + disabled (mirrors handlePortfolioChange in sigma-index.js).
  const lk = (node: React.ReactNode) => <div className={pfLocked ? 'pe-none opacity-50' : ''}>{node}</div>;

  const fields: (React.ReactNode | FilterField)[] = [
    {
      node: (
        <div key="date">
          <label className="form-label">Date Time Range</label>
          <DateRangePicker
            mode="range"

            enableTime={intraday}
            timeSeconds={intraday}
            value={{ start: draft.startDate, end: draft.endDate }}
            onChange={(v) => {
              const end = v.end;
              const start = v.start > end ? end : v.start;
              setDraft((f) => ({ ...f, startDate: start, endDate: end }));
            }}
          />
        </div>
      ),
      pinned: true,
      width: 270,
    },
    <Select2
      key="interval"
      label="Interval"
     
      searchable={false}
      allowClear={false}
      value={str(draft.interval)}
      options={INTERVAL_OPTIONS}
      onChange={(v) => {
        const interval = Number(v) || 1440;
        setDraft((f) => ({ ...f, interval, live: interval >= 1440 ? false : f.live }));
      }}
    />,
    <div key="symbols">
      <label className="form-label">Symbols</label>
      <Select2Multi placeholder="All" value={draft.symbols} options={instruments.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, symbols: v }))} />
    </div>,
    lk(
      <>
        <label className="form-label">Group</label>
        <Select2Async placeholder="All" value={str(draft.group) || null} fetch={filterDataPaged.groups} onChange={(v) => setDraft((f) => ({ ...f, group: v || null }))} />
      </>,
    ),
    lk(<Select2 label="MiFID" allowClear placeholder="All" value={str(draft.mifidClassification)} options={mifid.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, mifidClassification: v || null }))} />),
    lk(<Select2 label="Execution Venue" allowClear placeholder="All" value={str(draft.executionVenue)} options={venues.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, executionVenue: v || null }))} />),
    lk(<Select2 label="Group Profile" allowClear placeholder="All" value={str(draft.groupProfile)} options={groupProfiles.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, groupProfile: v || null }))} />),
    lk(
      <>
        <label className="form-label">Group Text Filter</label>
        <input className="form-control" placeholder="S*, *abc*, !SPOT" value={draft.groupText ?? ''} onChange={(e) => setDraft((f) => ({ ...f, groupText: e.target.value || null }))} />
      </>,
    ),
    lk(
      <>
        <label className="form-label">Country</label>
        <Select2Async placeholder="All" value={str(draft.countryId) || null} fetch={filterDataPaged.countries} onChange={(v) => setDraft((f) => ({ ...f, countryId: v ? Number(v) : null }))} />
      </>,
    ),
    ...(!uid
      ? [
          <Select2
            key="portfolio"
            label="Portfolio Profile"
           
            allowClear
            placeholder="All"
            value={str(draft.portfolio)}
            options={portfolios.data ?? []}
            onChange={(v) =>
              setDraft((f) => {
                const portfolio = v ? Number(v) : null;
                // Selecting a portfolio clears the other dimension filters.
                return portfolio != null
                  ? { ...f, portfolio, group: null, groupText: null, groupProfile: null, mifidClassification: null, executionVenue: null, countryId: null, login: null, connectorId: null, uniqueId: null }
                  : { ...f, portfolio };
              })
            }
          />,
          <div className={pfLocked ? 'pe-none opacity-50' : ''} key="login">
            <label className="form-label">Login</label>
            <input className="form-control" inputMode="numeric" value={draft.login ?? ''} onChange={(e) => setDraft((f) => ({ ...f, login: e.target.value ? Number(e.target.value) : null }))} />
          </div>,
          lk(<Select2 key="connector" label="Connector" allowClear placeholder="All" value={str(draft.connectorId)} options={connectors.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, connectorId: v ? Number(v) : null }))} />),
        ]
      : [
          <div key="uniqueId">
            <label className="form-label">Unique ID</label>
            <input className="form-control" value={draft.uniqueId ?? ''} onChange={(e) => setDraft((f) => ({ ...f, uniqueId: e.target.value || null }))} />
          </div>,
        ]),
    <div key="highlight">
      <label className="form-label">Sigma Action Thresholds</label>
      <input className="form-control" inputMode="decimal" value={draft.highlight ?? ''} onChange={(e) => setDraft((f) => ({ ...f, highlight: e.target.value === '' ? null : Number(e.target.value) }))} />
    </div>,
  ];

  return (
    <FilterBar
      fields={fields}
      visibleCount={4}
      modes={
        <>
          {canConsolidated && <Switch label="By UID" on={draft.byUniqueId} onToggle={(c) => setDraft((f) => ({ ...f, byUniqueId: c, login: null, connectorId: null, portfolio: null, uniqueId: null }))} />}
          <Switch label="Cumulative" on={draft.cumulativeProfit} onToggle={(c) => setDraft((f) => ({ ...f, cumulativeProfit: c }))} />
          <Switch label="Live" on={draft.live} disabled={!intraday} onToggle={(c) => setDraft((f) => ({ ...f, live: c }))} />
        </>
      }
      actions={
        <>
          <button type="button" className="btn btn-primary btn-sm" onClick={onFetch} disabled={fetching}>
            {fetching ? <i className="fa fa-spinner fa-spin me-1" /> : <i className="fa fa-search me-1" />}
            Fetch
          </button>
          <AsyncButton className="btn btn-outline-primary btn-sm" icon="fa fa-download" onClick={onExtract}>
            Extract
          </AsyncButton>
        </>
      }
    />
  );
}

function Switch({ label, on, onToggle, disabled }: { label: string; on: boolean; onToggle: (c: boolean) => void; disabled?: boolean }) {
  const id = `sg-switch-${label.replace(/\s+/g, '')}`;
  return (
    <div className="form-check form-switch mb-0">
      <input className="form-check-input" type="checkbox" role="switch" id={id} checked={on} disabled={disabled} onChange={(e) => onToggle(e.target.checked)} />
      <label className="form-check-label small" htmlFor={id}>{label}</label>
    </div>
  );
}
